'use client';

import type { Variants } from 'motion/react';
import { motion, useAnimation } from 'motion/react';
import type { HTMLAttributes } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import { cn } from '@/lib/utils';

export interface ArrowBigUpIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface ArrowBigUpIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
}

const pathVariants: Variants = {
  normal: { d: 'M9 18v-6H5l7-7 7 7h-4v6H9z', translateY: 0 },
  animate: {
    d: 'M9 18v-6H5l7-7 7 7h-4v6H9z',
    translateY: [0, -4, 0],
    transition: {
      duration: 0.9,
      repeat: Number.POSITIVE_INFINITY,
      repeatType: 'loop',
      ease: 'easeInOut',
    },
  },
};

const ArrowBigUpIcon = forwardRef<ArrowBigUpIconHandle, ArrowBigUpIconProps>(
  ({ className, size = 28, ...props }, ref) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;
      return {
        startAnimation: () => controls.start('animate'),
        stopAnimation: () => controls.start('normal'),
      };
    });

    useEffect(() => {
      if (!isControlledRef.current) {
        controls.start('animate');
      }
    }, [controls]);

    return (
      <div className={cn(className)} {...props}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round">
          <motion.path
            d="M9 18v-6H5l7-7 7 7h-4v6H9z"
            variants={pathVariants}
            animate={controls}
          />
        </svg>
      </div>
    );
  },
);

ArrowBigUpIcon.displayName = 'ArrowBigUpIcon';

export { ArrowBigUpIcon };
